import { useUser } from '@clerk/clerk-react';
import { useCallback, useEffect, useRef } from 'react';

/**
 * Which tool guides this person has already been shown on their own, once in their
 * lifetime (Tarek, 2026-09-30): kept on their Flobi account, so a second computer does not
 * introduce every tool again, and in this browser too, so it is known before the account
 * has answered and still known without it.
 */
const STORE = 'artwork.guides.seen';
const FIELD = 'artworkGuidesSeen';

function readLocal(): Set<string> {
  try {
    const raw = JSON.parse(localStorage.getItem(STORE) ?? '[]');
    return new Set(Array.isArray(raw) ? raw.filter((one): one is string => typeof one === 'string') : []);
  } catch {
    return new Set();
  }
}
function writeLocal(seen: ReadonlySet<string>): void {
  try {
    localStorage.setItem(STORE, JSON.stringify([...seen]));
  } catch {
    // Private windows refuse storage; the account still remembers.
  }
}

/** Merges what the account remembers into what this browser does. */
export function mergeSeen(local: ReadonlySet<string>, account: unknown): Set<string> {
  const merged = new Set(local);
  if (Array.isArray(account)) for (const one of account) if (typeof one === 'string') merged.add(one);
  return merged;
}

export function useSeenGuides(): { has(key: string): boolean; mark(key: string): void } {
  const { user, isLoaded } = useUser();
  const seen = useRef<Set<string>>(readLocal());
  const saving = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isLoaded || !user) return;
    const merged = mergeSeen(seen.current, user.unsafeMetadata?.[FIELD]);
    seen.current = merged;
    writeLocal(merged);
  }, [isLoaded, user]);

  const has = useCallback((key: string) => seen.current.has(key), []);
  const mark = useCallback((key: string) => {
    if (seen.current.has(key)) return;
    seen.current.add(key);
    writeLocal(seen.current);
    if (!user) return;
    // One write for a burst of first picks, not one per tool.
    if (saving.current) clearTimeout(saving.current);
    saving.current = setTimeout(() => {
      saving.current = null;
      const all = [...mergeSeen(seen.current, user.unsafeMetadata?.[FIELD])];
      void user.update({ unsafeMetadata: { ...user.unsafeMetadata, [FIELD]: all } }).catch(() => undefined);
    }, 1500);
  }, [user]);

  return { has, mark };
}
